import { z } from "zod";
import { convergenceProposalOperationSchema, convergenceProposalStatusSchema, convergenceProposalTargetKindSchema, twinProductContractSchema, twinProductEvaluationContractSchema, twinProductEvaluationSuiteSchema, } from "./convergence.js";
import { twinRunEvaluationEvidenceRefSchema } from "./evaluation-evidence.js";
import { twinRunEvaluationAuthoritySchema } from "./twin-run-evaluation-authority.js";
const jsonObjectSchema = z.record(z.string(), z.unknown());
const idSchema = z.string().trim().min(1).max(160);
const slugSchema = z
    .string()
    .trim()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u, "Expected kebab-case slug");
const sqlIdentifierSchema = z
    .string()
    .trim()
    .min(1)
    .max(63)
    .regex(/^[a-z][a-z0-9_]*$/u, "Expected snake_case SQL identifier");
const timestampMsSchema = z.number().int().nonnegative();
const optionalTimestampMsSchema = timestampMsSchema.nullable();
const externalIdSchema = z.string().trim().min(1).max(512);
export const twinDefinitionStatusSchema = z.enum([
    "hypothesis",
    "verified",
    "adjudicated",
    "archived",
]);
export const twinDerivedRelationStatusSchema = z.enum(["draft", "active", "archived"]);
export const twinProjectionRunStatusSchema = z.enum(["running", "succeeded", "failed"]);
export const twinRelationshipRuleSchema = z.object({
    id: idSchema,
    slug: slugSchema,
    version: z.number().int().positive(),
    status: twinDefinitionStatusSchema,
    source: z.string().trim().min(1).max(120),
    entity: z.string().trim().min(1).max(120),
    quantification: z.literal("open_world"),
    updatedAt: timestampMsSchema,
});
export const twinDerivedRelationSchema = z.object({
    id: idSchema,
    slug: slugSchema,
    version: z.number().int().positive(),
    status: twinDerivedRelationStatusSchema,
    narrative: z.string().max(12_000),
    relationSchema: sqlIdentifierSchema,
    relationName: sqlIdentifierSchema,
    lastRefreshedAt: optionalTimestampMsSchema,
    sourceWatermark: optionalTimestampMsSchema,
    examples: z.array(z.unknown()),
    stats: jsonObjectSchema,
    updatedAt: timestampMsSchema,
});
export const twinProcessDefinitionSchema = z.object({
    id: idSchema,
    slug: slugSchema,
    version: z.number().int().positive(),
    status: twinDefinitionStatusSchema,
    definition: jsonObjectSchema,
    updatedAt: timestampMsSchema,
});
export const twinProjectionRunSchema = z.object({
    id: idSchema,
    kind: z.enum(["relationships", "process"]),
    status: twinProjectionRunStatusSchema,
    coverage: z.number().min(0).max(1).nullable(),
    provenanceCoverage: z.number().min(0).max(1).nullable(),
    fitness: z.number().min(0).max(1).nullable(),
    residueCount: z.number().int().nonnegative(),
    rowsWritten: z.number().int().nonnegative(),
    startedAt: timestampMsSchema,
    finishedAt: optionalTimestampMsSchema,
    error: z.string().nullable(),
    stats: jsonObjectSchema,
});
export const twinRelationshipSampleSchema = z.object({
    relationName: sqlIdentifierSchema,
    subjectEntityId: idSchema,
    subjectDisplayName: z.string().trim().min(1).max(512).nullable(),
    predicate: z.string().trim().min(1).max(200),
    objectEntityId: idSchema,
    objectDisplayName: z.string().trim().min(1).max(512).nullable(),
    observationCount: z.number().int().nonnegative(),
    firstSeen: timestampMsSchema,
    lastSeen: timestampMsSchema,
    totalWeight: z.number(),
    status: z.string().trim().min(1).max(80),
});
export const twinResidueSampleSchema = z.object({
    externalId: externalIdSchema,
    occurredAt: timestampMsSchema,
    createdAt: timestampMsSchema,
    payloadJson: z.unknown(),
});
export const twinResidueBucketSchema = z.object({
    source: z.string().trim().min(1).max(120),
    entity: z.string().trim().min(1).max(120),
    count: z.number().int().nonnegative(),
    samples: z.array(twinResidueSampleSchema),
});
export const twinPendingProposalSchema = z.object({
    id: idSchema,
    title: z.string().trim().min(1).max(240),
    rationale: z.string().trim().min(1).max(4000),
    status: convergenceProposalStatusSchema,
    operation: convergenceProposalOperationSchema,
    targetKind: convergenceProposalTargetKindSchema,
    evidence: jsonObjectSchema,
    proposedPatch: jsonObjectSchema,
    reviewNote: z.string().nullable(),
    reviewedAt: optionalTimestampMsSchema,
    failureMessage: z.string().nullable(),
    createdAt: timestampMsSchema,
    updatedAt: timestampMsSchema,
});
export const twinRunEvaluationModeSchema = z.enum(["manual", "eval", "canary"]);
export const twinRunEvaluationVerdictSchema = z.enum(["passed", "failed", "inconclusive"]);
export const twinRunEvaluationCheckSchema = z.object({
    key: z.enum([
        "run_completed", "answer_present", "product_bound", "product_evidence",
        "product_citation", "tool_budget", "expected_facts", "qualitative_criteria",
        "coverage_claims",
    ]),
    status: z.enum(["passed", "failed", "skipped"]),
    detail: z.string().trim().min(1).max(2000),
}).strict();
export const requestTwinRunEvaluationInputSchema = z.object({
    runId: idSchema,
    mode: twinRunEvaluationModeSchema,
    productSlug: slugSchema.optional(),
    candidateReleaseSha: z.string().regex(/^[0-9a-f]{7,64}$/u).optional(),
    evalReference: z.object({
        suiteId: slugSchema,
        suiteVersion: z.number().int().positive(),
        caseId: slugSchema,
    }).strict().optional(),
}).strict().superRefine((input, context) => {
    if ((input.mode === "eval" || input.mode === "canary") && !input.evalReference) {
        context.addIssue({ code: "custom", path: ["evalReference"], message: `${input.mode} mode requires an eval reference.` });
    }
    if (input.mode === "manual" && input.candidateReleaseSha) {
        context.addIssue({
            code: "custom",
            path: ["candidateReleaseSha"],
            message: "manual evaluations cannot mint deploy receipts.",
        });
    }
});
export const twinRunEvaluationSchema = z.object({
    id: idSchema,
    tenantSlug: idSchema,
    organisationId: idSchema,
    taskRunId: idSchema,
    productPublicationId: idSchema.nullable(),
    productPublicationVersionId: idSchema.nullable(),
    viewContractAuthority: twinRunEvaluationAuthoritySchema.optional(),
    productSlug: slugSchema,
    productVersion: z.number().int().positive(),
    mode: twinRunEvaluationModeSchema,
    evalReference: z.object({
        suiteId: slugSchema,
        suiteVersion: z.number().int().positive(),
        caseId: slugSchema,
    }).strict().nullable(),
    contractHash: z.string().regex(/^[a-f0-9]{64}$/u),
    outputHash: z.string().regex(/^[a-f0-9]{64}$/u).nullable(),
    evidenceHash: z.string().regex(/^[a-f0-9]{64}$/u).nullable(),
    evidenceCaptureStatus: z.enum(["captured", "legacy_unavailable"]),
    evaluatorEngineVersion: z.number().int().positive(),
    // Missing scope belongs to historical receipts; it must never imply answer validation.
    scope: z.enum(["runtime_behavior", "answer_contract", "unknown"]).optional(),
    scopeVersion: z.number().int().positive().nullable().optional(),
    runStatus: z.enum(["completed", "failed", "cancelled"]),
    resolvedSkillVersionIds: z.array(idSchema),
    toolCallCount: z.number().int().nonnegative(),
    evidenceRefs: z.array(twinRunEvaluationEvidenceRefSchema),
    verdict: twinRunEvaluationVerdictSchema,
    checks: z.array(twinRunEvaluationCheckSchema),
    summary: z.string().trim().min(1).max(8000),
    createdBy: idSchema,
    createdAt: timestampMsSchema,
    updatedAt: timestampMsSchema,
}).strict().superRefine((value, context) => {
    if (value.viewContractAuthority
        ? value.productPublicationId !== null || value.productPublicationVersionId !== null
        : value.productPublicationId === null) {
        context.addIssue({ code: "custom", message: "Evaluation requires exactly one historical publication or current view contract authority." });
    }
    const answerTest = value.viewContractAuthority?.answerTest;
    if (answerTest ? value.scope !== "answer_contract" || value.contractHash !== answerTest.suiteSha256 ||
        value.evalReference?.caseId !== answerTest.caseId : value.viewContractAuthority && value.scope === "answer_contract") {
        context.addIssue({ code: "custom", message: "Current answer evaluation must bind its exact published test and case." });
    }
    if (answerTest && (value.verdict === "passed" && answerTest.facts.some(fact => fact.status !== "passed") ||
        value.verdict !== "failed" && answerTest.facts.some(fact => fact.status === "failed"))) {
        context.addIssue({ code: "custom", message: "Answer verdict must agree with the declared fact outcomes." });
    }
});
export const twinRunEvaluationResponseSchema = z.object({
    evaluations: z.array(twinRunEvaluationSchema).min(1),
}).strict();
export const twinRunEvaluationHistoryResponseSchema = z.object({
    evaluations: z.array(twinRunEvaluationSchema),
}).strict();
export const twinEvaluationSuitePublicationSchema = z.object({
    productPublicationId: idSchema,
    productPublicationVersionId: idSchema,
    productSlug: slugSchema,
    productVersion: z.number().int().positive(),
    productSkillSlugs: z.array(slugSchema).min(1).max(32),
    evaluation: twinProductEvaluationContractSchema,
    suite: twinProductEvaluationSuiteSchema,
}).strict();
export const twinDiscoveryModeSchema = z.enum(["automatic", "guided", "explicit"]);
export const twinDiscoveryScopeSchema = z.enum(["relationships", "metrics", "products"]);
export const twinMetricStorageKindSchema = z.enum(["view", "materialized_view"]);
export const twinMetricValidationModeSchema = z.enum(["exact", "containment"]);
export const twinGroundTruthUsageSchema = z.enum(["validation_only", "adopt_as_product"]);
export const twinCandidateSourceModeSchema = z.enum(["catalog_or_raw", "raw_events_only"]);
const discoveryTextSchema = z.string().trim().min(1).max(1000);
const discoveryTextListSchema = z.array(discoveryTextSchema).max(100).default([]);
export const twinDiscoveryGuidanceSchema = z.object({
    targetProductSlug: slugSchema.optional(),
    validationStatementTimeoutMs: z.number().int().min(1_000).max(300_000).optional(),
    metricStorageKind: twinMetricStorageKindSchema.optional(),
    metricValidationMode: twinMetricValidationModeSchema.optional(),
    groundTruthUsage: twinGroundTruthUsageSchema.default("validation_only"),
    candidateSourceMode: twinCandidateSourceModeSchema.optional(),
    productContract: twinProductContractSchema.optional(),
    evaluationSuites: z.array(twinProductEvaluationSuiteSchema).max(100).default([]),
    supportedIntents: discoveryTextListSchema,
    unsupportedIntents: discoveryTextListSchema,
    includedSources: discoveryTextListSchema,
    includedEntities: discoveryTextListSchema,
    excludedEntities: discoveryTextListSchema,
    canonicalDefinitions: z.array(z.object({
        name: z.string().trim().min(1).max(160),
        definition: discoveryTextSchema,
    }).strict()).max(100).default([]),
    groundTruthRelations: z.array(z.object({
        schema: sqlIdentifierSchema,
        name: sqlIdentifierSchema,
        purpose: discoveryTextSchema,
    }).strict()).max(100).default([]),
    negativeControlRelations: z.array(z.object({
        schema: sqlIdentifierSchema,
        name: sqlIdentifierSchema,
        purpose: discoveryTextSchema,
        matchColumns: z.array(sqlIdentifierSchema).min(1).max(20)
            .refine((columns) => new Set(columns).size === columns.length, "Negative-control match columns must be unique"),
        diagnosticCategoryColumn: sqlIdentifierSchema.optional(),
    }).strict()).max(100).default([]),
    candidateSourceRelations: z.array(z.object({
        schema: sqlIdentifierSchema,
        name: sqlIdentifierSchema,
    }).strict()).max(100).default([]),
    requiredDimensions: discoveryTextListSchema,
    requiredMeasures: discoveryTextListSchema,
    guidanceRefs: z.array(z.string().trim().min(1).max(512)).max(100).default([]),
}).strict().superRefine((guidance, context) => {
    if (guidance.groundTruthUsage === "adopt_as_product" && guidance.groundTruthRelations.length !== 1) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groundTruthRelations"],
            message: "Ground-truth adoption requires exactly one governed relation.",
        });
    }
    if (guidance.groundTruthUsage === "adopt_as_product"
        && guidance.metricValidationMode === "containment") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groundTruthUsage"],
            message: "Ground-truth adoption requires exact validation; containment evidence is validation-only.",
        });
    }
    if (guidance.metricValidationMode === "containment"
        && guidance.candidateSourceRelations.length === 0
        && guidance.candidateSourceMode !== "raw_events_only") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["candidateSourceRelations"],
            message: "Containment validation requires independently declared candidate source relations.",
        });
    }
    if (guidance.metricValidationMode === "containment" && guidance.groundTruthRelations.length === 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["groundTruthRelations"],
            message: "Containment validation requires at least one independent verified subset relation. " +
                "It must contain an organisation_id-scoped row and use the candidate's exact ordered columns and PostgreSQL types.",
        });
    }
    const candidateSources = new Set(guidance.candidateSourceRelations.map((relation) => `${relation.schema}.${relation.name}`));
    if (guidance.candidateSourceMode === "raw_events_only" && candidateSources.size > 0) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["candidateSourceRelations"],
            message: "Raw-event-only authoring cannot also declare governed candidate source relations.",
        });
    }
    if (guidance.metricValidationMode === "containment") {
        for (const [index, relation] of guidance.groundTruthRelations.entries()) {
            if (!candidateSources.has(`${relation.schema}.${relation.name}`))
                continue;
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["groundTruthRelations", index],
                message: "Containment ground truth must be an independent verified subset, not a candidate source relation. " +
                    "Create a separate organisation-scoped validation relation with at least one row and the candidate's exact ordered columns and PostgreSQL types; every validation row must occur in the candidate.",
            });
        }
    }
    for (const [index, relation] of guidance.negativeControlRelations.entries()) {
        if (!candidateSources.has(`${relation.schema}.${relation.name}`))
            continue;
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["negativeControlRelations", index],
            message: "Negative-control relations cannot also be candidate source relations.",
        });
    }
});
function hasGuidedDiscoveryCharter(guidance) {
    return Boolean(guidance.targetProductSlug
        || guidance.productContract
        || guidance.supportedIntents.length
        || guidance.unsupportedIntents.length
        || guidance.includedSources.length
        || guidance.includedEntities.length
        || guidance.excludedEntities.length
        || guidance.canonicalDefinitions.length
        || guidance.groundTruthRelations.length
        || guidance.negativeControlRelations.length
        || guidance.candidateSourceRelations.length
        || guidance.requiredDimensions.length
        || guidance.requiredMeasures.length
        || guidance.guidanceRefs.length
        || guidance.evaluationSuites.length);
}
const discoveryScopesSchema = z.array(twinDiscoveryScopeSchema).min(1).max(3)
    .refine((scopes) => new Set(scopes).size === scopes.length, "Discovery scopes must be unique");
const discoveryGoalSchema = z.string().trim().min(1).max(4000);
const explicitProposalIdsSchema = z.array(idSchema).min(1).max(100)
    .refine((ids) => new Set(ids).size === ids.length, "Explicit proposal ids must be unique");
export const startTwinDiscoveryRunInputSchema = z.discriminatedUnion("mode", [
    z.object({
        mode: z.literal("automatic"),
        scopes: discoveryScopesSchema,
        goal: discoveryGoalSchema.optional(),
        guidance: twinDiscoveryGuidanceSchema.optional(),
    }).strict(),
    z.object({
        mode: z.literal("guided"),
        scopes: discoveryScopesSchema,
        goal: discoveryGoalSchema,
        guidance: twinDiscoveryGuidanceSchema,
    }).strict().refine((input) => hasGuidedDiscoveryCharter(input.guidance), { message: "Guided discovery requires at least one guidance charter entry", path: ["guidance"] }),
    z.object({
        mode: z.literal("explicit"),
        scopes: discoveryScopesSchema,
        goal: discoveryGoalSchema.optional(),
        guidance: twinDiscoveryGuidanceSchema.optional(),
        explicitProposalIds: explicitProposalIdsSchema,
    }).strict(),
]);
export const resumeTwinDiscoveryRunInputSchema = z.object({
    expectedUpdatedAt: timestampMsSchema,
    guidance: twinDiscoveryGuidanceSchema,
}).strict().superRefine((input, context) => {
    if (input.guidance.groundTruthUsage !== "validation_only") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guidance", "groundTruthUsage"],
            message: "Evidence resumption requires validation-only ground truth.",
        });
    }
    if (input.guidance.groundTruthRelations.length !== 1) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["guidance", "groundTruthRelations"],
            message: "Evidence resumption currently requires exactly one explicitly declared ground-truth relation.",
        });
    }
});
export const operatorResumeTwinDiscoveryRunInputSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
    expectedUpdatedAt: timestampMsSchema,
    guidance: twinDiscoveryGuidanceSchema,
}).strict();
export const twinDiscoveryRunStatusSchema = z.enum([
    "queued", "running", "needs_evidence", "succeeded", "failed", "cancelled",
]);
export const twinDiscoveryRunPhaseSchema = z.enum([
    "queued", "inventory", "relationships", "relationship_evaluation",
    "metrics", "products", "evidence_required", "finalizing", "completed",
]);
export const twinDiscoveryProgressSchema = z.object({
    completedScopes: z.array(twinDiscoveryScopeSchema),
    totalScopes: z.number().int().min(1).max(3),
    proposalCount: z.number().int().nonnegative(),
    evaluatedProposalCount: z.number().int().nonnegative(),
    phaseDetail: z.string().trim().max(1000).nullable(),
}).strict();
export const twinDiscoveryRunSchema = z.object({
    id: idSchema,
    tenantSlug: idSchema,
    organisationId: idSchema,
    mode: twinDiscoveryModeSchema,
    scopes: discoveryScopesSchema,
    goal: discoveryGoalSchema.nullable(),
    guidance: twinDiscoveryGuidanceSchema.nullable(),
    explicitProposalIds: z.array(idSchema),
    status: twinDiscoveryRunStatusSchema,
    phase: twinDiscoveryRunPhaseSchema,
    progress: twinDiscoveryProgressSchema,
    proposalIds: z.array(idSchema),
    result: jsonObjectSchema,
    errorMessage: z.string().nullable(),
    requestedBy: idSchema,
    requestedAt: timestampMsSchema,
    startedAt: optionalTimestampMsSchema,
    finishedAt: optionalTimestampMsSchema,
    updatedAt: timestampMsSchema,
    cancelRequestedAt: optionalTimestampMsSchema,
}).strict();
export const twinDiscoveryDiagnosticSchema = z.object({
    id: idSchema,
    runId: idSchema,
    scope: twinDiscoveryScopeSchema,
    candidateIndex: z.number().int().nonnegative(),
    round: z.enum(["initial", "repair"]),
    candidateHash: z.string().regex(/^[a-f0-9]{64}$/u),
    sqlHash: z.string().regex(/^[a-f0-9]{64}$/u).nullable(),
    reason: z.string().min(1),
    candidate: jsonObjectSchema,
    createdAt: timestampMsSchema,
}).strict();
export const listTwinDiscoveryDiagnosticsResponseSchema = z.object({
    diagnostics: z.array(twinDiscoveryDiagnosticSchema),
}).strict();
export const twinDiscoveryRunResponseSchema = z.object({
    discoveryRun: twinDiscoveryRunSchema,
}).strict();
export const listTwinDiscoveryRunsResponseSchema = z.object({
    discoveryRuns: z.array(twinDiscoveryRunSchema),
}).strict();
export const listTwinDiscoveryRunsQuerySchema = z.object({
    status: twinDiscoveryRunStatusSchema.optional(),
    mode: twinDiscoveryModeSchema.optional(),
    limit: z.coerce.number().int().min(1).max(100).default(25),
}).strict();
export const operatorTwinScopeSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
}).strict();
export const operatorStartTwinDiscoveryRunInputSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
}).passthrough().transform((input, context) => {
    const { tenantSlug, organisationId, ...discovery } = input;
    const parsed = startTwinDiscoveryRunInputSchema.safeParse(discovery);
    if (!parsed.success) {
        context.addIssue({
            code: "custom",
            message: parsed.error.issues.map((issue) => issue.message).join("; "),
        });
        return z.NEVER;
    }
    return { tenantSlug, organisationId, ...parsed.data };
});
export const twinArchetypeProviderSchema = z.enum(["xero", "sun", "overture"]);
export const operatorReconcileTwinArchetypeInputSchema = operatorTwinScopeSchema.extend({
    provider: twinArchetypeProviderSchema,
    version: z.number().int().positive().optional(),
}).strict();
const twinArchetypeDiffSummarySchema = z.object({
    missing: z.number().int().nonnegative(),
    changed: z.number().int().nonnegative(),
    extra: z.number().int().nonnegative(),
}).strict();
export const reconcileTwinArchetypeResponseSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
    provider: twinArchetypeProviderSchema,
    version: z.number().int().positive(),
    before: twinArchetypeDiffSummarySchema,
    applied: z.object({
        relationshipRules: z.number().int().nonnegative(),
        processDefinitions: z.number().int().nonnegative(),
        processBindings: z.number().int().nonnegative(),
    }).strict(),
    archived: z.object({
        relationshipRules: z.number().int().nonnegative(),
        processDefinitions: z.number().int().nonnegative(),
    }).strict(),
    after: twinArchetypeDiffSummarySchema,
    reconciled: z.literal(true),
}).strict();
export const twinFleetOrganisationReadinessSchema = z.enum([
    "ready", "needs_discovery", "needs_guidance", "blocked",
]);
export const twinFleetOrganisationSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
    name: z.string().trim().min(1).max(240),
    rawEventCount: z.number().int().nonnegative(),
    relationshipRuleCount: z.number().int().nonnegative(),
    coverage: z.number().min(0).max(1).nullable(),
    residueCount: z.number().int().nonnegative(),
    publishedProductCount: z.number().int().nonnegative(),
    pendingProposalCount: z.number().int().nonnegative(),
    activeRunCount: z.number().int().nonnegative(),
    readiness: twinFleetOrganisationReadinessSchema,
    latestRun: twinDiscoveryRunSchema.nullable(),
}).strict();
export const twinFleetTargetSchema = z.object({
    targetId: idSchema,
    name: z.string().trim().min(1).max(240),
    baseUrl: z.string().url(),
    appUrl: z.string().url().nullable().optional(),
    reachable: z.boolean(),
    release: z.string().trim().min(1).max(160).nullable(),
    error: z.string().max(2000).nullable(),
    organisations: z.array(twinFleetOrganisationSchema),
}).strict();
export const twinFleetOverviewResponseSchema = z.object({
    generatedAt: timestampMsSchema,
    summary: z.object({
        targets: z.number().int().nonnegative(),
        reachableTargets: z.number().int().nonnegative(),
        organisations: z.number().int().nonnegative(),
        readyOrganisations: z.number().int().nonnegative(),
        activeRuns: z.number().int().nonnegative(),
        pendingProposals: z.number().int().nonnegative(),
    }).strict(),
    targets: z.array(twinFleetTargetSchema),
    /** Whether the caller may start discovery from this view; absent means unknown (older servers). */
    dispatch: z.object({
        available: z.boolean(),
        reason: z.string().max(240).optional(),
    }).strict().optional(),
}).strict();
export const twinTargetOverviewResponseSchema = z.object({
    generatedAt: timestampMsSchema,
    organisations: z.array(twinFleetOrganisationSchema),
}).strict();
export const startTwinFleetDiscoveryInputSchema = z.object({
    organisations: z.array(z.object({
        targetId: idSchema,
        tenantSlug: idSchema,
        organisationId: idSchema,
    }).strict()).min(1).max(500),
    discovery: startTwinDiscoveryRunInputSchema,
}).strict();
export const twinOverviewSummarySchema = z.object({
    relationshipRules: z.number().int().nonnegative(),
    verifiedRelationshipRules: z.number().int().nonnegative(),
    derivedRelations: z.number().int().nonnegative(),
    activeDerivedRelations: z.number().int().nonnegative(),
    processDefinitions: z.number().int().nonnegative(),
    verifiedProcessDefinitions: z.number().int().nonnegative(),
    latestProjectionRunStatus: twinProjectionRunStatusSchema.nullable(),
});
export const twinSourceSummarySchema = z.object({
    source: z.string().trim().min(1).max(160),
    entityCount: z.number().int().nonnegative(),
    recordCount: z.number().int().nonnegative(),
}).strict();
export const twinChannelUseSchema = z.object({
    channelId: idSchema,
    channelName: z.string().trim().min(1).max(240),
    datasetId: idSchema,
    productSlug: slugSchema,
    viewCount: z.number().int().nonnegative(),
}).strict();
export const twinOverviewResponseSchema = z.object({
    tenantSlug: idSchema,
    organisationId: idSchema,
    generatedAt: timestampMsSchema,
    summary: twinOverviewSummarySchema,
    relationshipRules: z.array(twinRelationshipRuleSchema),
    derivedRelations: z.array(twinDerivedRelationSchema),
    processDefinitions: z.array(twinProcessDefinitionSchema),
    projectionRuns: z.array(twinProjectionRunSchema),
    relationshipSamples: z.array(twinRelationshipSampleSchema),
    residueBuckets: z.array(twinResidueBucketSchema),
    pendingProposals: z.array(twinPendingProposalSchema),
    sources: z.array(twinSourceSummarySchema),
    channelUses: z.array(twinChannelUseSchema),
});
const foldRequestTokenSchema = z.string()
    .trim()
    .min(1)
    .max(160)
    .regex(/^[A-Za-z0-9][A-Za-z0-9._:-]*$/u, "Expected an idempotency token");
const uniqueFoldRequestValues = (values, ctx) => {
    if (new Set(values).size !== values.length) {
        ctx.addIssue({ code: "custom", message: "Values must be unique" });
    }
};
export const twinFoldRequestSchema = z.object({
    organisationId: idSchema,
    clientRequestId: foldRequestTokenSchema,
    reason: z.string().trim().min(1).max(1000),
    sourceConnectorJobIds: z.array(idSchema).min(1).max(50)
        .superRefine(uniqueFoldRequestValues),
    deferredEntities: z.array(idSchema).max(50)
        .superRefine(uniqueFoldRequestValues),
}).strict();
export const twinFoldRequestReceiptSchema = z.object({
    id: idSchema,
    tenantSlug: idSchema,
    organisationId: idSchema,
    clientRequestId: foldRequestTokenSchema,
    foldJobId: idSchema,
    requestedGeneration: z.number().int().positive(),
    actorId: z.string().trim().min(1).max(240),
    reason: z.string().trim().min(1).max(1000),
    sourceConnectorJobIds: z.array(idSchema).min(1).max(50),
    deferredEntities: z.array(idSchema).max(50),
    createdAt: timestampMsSchema,
}).strict();
export const twinFoldRequestResponseSchema = z.object({
    receipt: twinFoldRequestReceiptSchema,
    replayed: z.boolean(),
}).strict();
